import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const ShapeYogaGame = ({ gameData, onComplete }) => {
    const [currentPoseIndex, setCurrentPoseIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [gameState, setGameState] = useState('intro'); // 'intro', 'holding', 'success'
    const [timer, setTimer] = useState(0);

    const currentPose = gameData?.poses?.[currentPoseIndex];

    useEffect(() => {
        let timerId;
        if (gameState === 'holding') {
            if (timer > 0) {
                timerId = setTimeout(() => {
                    setTimer(timer - 1);
                    triggerHaptic(10);
                }, 1000);
            } else {
                handleSuccess();
            }
        }
        return () => clearTimeout(timerId);
    }, [gameState, timer]);

    const startPose = () => {
        setGameState('holding');
        setTimer(currentPose.hold_time || 10);
        triggerHaptic(100);
    };

    const handleSuccess = () => {
        setGameState('success');
        const pointsToAdd = gameData.points_per_pose || 15;
        const newScore = score + pointsToAdd;
        setScore(newScore);
        triggerHaptic([100, 50, 100]);
    };

    const nextLevel = () => {
        if (currentPoseIndex < gameData.poses.length - 1) {
            setCurrentPoseIndex(prev => prev + 1);
            setGameState('intro');
            triggerHaptic(30);
        } else {
            triggerHaptic([200, 100, 200, 100, 300]);
            onComplete?.(score);
        }
    };

    if (!gameData || !gameData.poses || !currentPose) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🧘</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Ruošiama zen erdvė...</h3>
            </div>
        );
    }

    const progress = (currentPoseIndex / gameData.poses.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-emerald-100 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border-2 border-emerald-200 animate-float">
                        🌿
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Figūrų <span className="text-emerald-600">Joga</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Energija</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Etapas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentPoseIndex + 1}/{gameData.poses.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8 flex flex-col items-center">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-4xl text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12">🧘</div>
                
                <div className="space-y-4 md:space-y-8 relative z-10 w-full">
                    <div className={`text-8xl md:text-[180px] drop-shadow-2xl transition-all duration-1000 ease-in-out ${gameState === 'holding' ? 'animate-float scale-110' : 'animate-pulse'}`}>
                        {currentPose.emoji}
                    </div>
                    <div className="space-y-2 md:space-y-4">
                        <h4 className="text-[8px] md:text-[10px] font-black text-emerald-400 uppercase tracking-[0.5em] mb-2">Pozos Vardas</h4>
                        <h2 className="text-xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none italic font-serif">
                            {currentPose.pose_name}
                        </h2>
                    </div>
                    <div className="bg-white/80 rounded-xl md:rounded-[2.5rem] p-4 md:p-8 shadow-xl border-2 border-emerald-50 relative group">
                        <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed italic font-serif px-4">
                            "{currentPose.instruction}"
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="flex flex-col items-center justify-center w-full max-w-4xl mx-auto py-4">
            <div className="pt-4 flex flex-col items-center w-full">
                {gameState === 'intro' && (
                    <button
                        onClick={startPose}
                        className="premium-button-3d group w-full py-8 md:py-12 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-4xl shadow-2xl transition-all border-b-8 md:border-b-[12px] border-emerald-800 uppercase tracking-[0.2em] relative overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                            PRADĖTI <span className="bg-white/20 px-4 py-1 rounded-xl text-lg md:text-3xl font-mono">{currentPose.hold_time || 10}S</span>
                        </span>
                    </button>
                )}

                {gameState === 'holding' && (
                    <div className="flex flex-col items-center space-y-6 md:space-y-12 animate-pop-in">
                        <div className="relative">
                            <svg className="w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 transform -rotate-90 filter drop-shadow-2xl">
                                <circle
                                    cx="50%"
                                    cy="50%"
                                    r="44%"
                                    stroke="currentColor"
                                    strokeWidth="12"
                                    fill="transparent"
                                    className="text-slate-100 opacity-20"
                                />
                                <circle
                                    cx="50%"
                                    cy="50%"
                                    r="44%"
                                    stroke="currentColor"
                                    strokeWidth="12"
                                    fill="transparent"
                                    strokeDasharray="276%"
                                    strokeDashoffset={`${276 - (timer / (currentPose.hold_time || 10)) * 276}%`}
                                    strokeLinecap="round"
                                    className="text-emerald-500 transition-all duration-1000 ease-linear"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-6xl md:text-9xl font-black text-emerald-600 animate-pulse tracking-tighter">{timer}</span>
                                <span className="text-[10px] md:text-sm font-black text-emerald-400 uppercase tracking-widest">Sekundės</span>
                            </div>
                        </div>
                        <div className="text-center space-y-2">
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-2 h-2 md:w-3 md:h-3 bg-emerald-500 rounded-full animate-ping"></div>
                                <p className="text-sm md:text-2xl font-black text-emerald-400 uppercase tracking-[0.4em]">Sulaikyk Poza...</p>
                            </div>
                            <p className="text-xs md:text-xl text-slate-400 font-bold italic font-serif">"Giliai kvėpuok ir nejudėk..."</p>
                        </div>
                    </div>
                )}

                {gameState === 'success' && (
                    <div className="animate-pop-in space-y-4 md:space-y-12 w-full">
                        <div className="text-6xl md:text-[120px] animate-bounce drop-shadow-2xl text-center">🌟</div>
                        <button
                            onClick={nextLevel}
                            className="premium-button-3d w-full py-8 md:py-12 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-4xl shadow-2xl transition-all border-b-8 md:border-b-[12px] border-indigo-800 uppercase tracking-[0.2em]"
                        >
                            KITA POZA ➡️
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {gameState === 'success' && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-3xl w-full p-8 md:p-20 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-emerald-500 text-center">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            ✨
                        </div>
                        
                        <div className="space-y-4 md:space-y-10 pt-4">
                            <h2 className="text-2xl md:text-6xl font-black uppercase tracking-tighter text-emerald-600 leading-none italic font-serif">
                                NUOSTABI HARMONIJA!
                            </h2>
                            <div className="p-6 md:p-12 rounded-2xl md:rounded-[4rem] bg-emerald-50 border-4 border-emerald-100 shadow-inner">
                                <p className="text-sm md:text-3xl font-bold text-slate-700 leading-relaxed font-serif italic">
                                    "Tavo kūnas ir protas tampa stipresni bei ramesni su kiekviena taisyklinga poza. Tikras jogos meistras!"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShapeYogaGame;
